'use client'
import React from 'react';
import { type Blog } from '@/lib/@types/api/blog';
import BlogItemDefault from "@/components/Blogs/BlogItemDefault";
import clsx from 'clsx';
import MyEmpty from '../MyEmpty';
import { useTranslations } from 'next-intl';
import { motion, AnimatePresence } from 'framer-motion';

interface BlogListProps {
	blogList: Blog.BlogListItem[];
	loading?: boolean;
}

// 骨架屏加载组件
const BlogSkeleton = () => {
	return (
		<div className="border block overflow-hidden rounded-lg bg-white shadow-lg relative">
			{/* 图片骨架 - 带闪烁效果 */}
			<div className="w-full h-[300px] bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer"></div>
			<div className="px-4 py-6">
				{/* 标签骨架 */}
				<div className="mb-3 flex gap-x-2">
					<div className="h-6 w-20 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] rounded-md animate-shimmer"></div>
					<div className="h-6 w-16 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] rounded-md animate-shimmer" style={{ animationDelay: '0.1s' }}></div>
				</div>
				{/* 标题骨架 */}
				<div className="mb-3 space-y-2">
					<div className="h-6 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] rounded w-full animate-shimmer" style={{ animationDelay: '0.2s' }}></div>
					<div className="h-6 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] rounded w-3/4 animate-shimmer" style={{ animationDelay: '0.3s' }}></div>
				</div>
				{/* 描述骨架 */}
				<div className="mb-5 space-y-2">
					<div className="h-4 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] rounded w-full animate-shimmer" style={{ animationDelay: '0.4s' }}></div>
					<div className="h-4 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] rounded w-5/6 animate-shimmer" style={{ animationDelay: '0.5s' }}></div>
				</div>
				{/* 底部元数据骨架 */}
				<div className="mt-3 flex items-center justify-between">
					<div className="flex items-center">
						<div className="w-12 h-12 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] rounded animate-shimmer" style={{ animationDelay: '0.6s' }}></div>
					</div>
					<div className="h-4 w-24 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] rounded animate-shimmer" style={{ animationDelay: '0.7s' }}></div>
				</div>
			</div>
		</div>
	);
};

const BlogList: React.FC<BlogListProps> = ({ blogList, loading = false }) => {
  const t = useTranslations();
  
  // 加载状态：显示骨架屏
  if (loading) {
    return (
      <div className="list-blog grid gap-5 grid-cols-3 max-md:grid-cols-1">
        {Array.from({ length: 9 }).map((_, index) => (
          <motion.div
            key={`skeleton-${index}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="w-full"
          >
            <BlogSkeleton />
          </motion.div>
        ))}
      </div>
    );
  }

  return blogList.length > 0 ? (
    <div className="list-blog grid gap-5 grid-cols-3 max-md:grid-cols-1">
      {blogList.map((item, index) => (
        <motion.div
          key={`${item.blog_slug}-${index}`}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -30 }}
          transition={{ 
            duration: 0.4, 
            delay: index * 0.05,
            ease: [0.4, 0, 0.2, 1]
          }}
          className="w-full"
        >
          <BlogItemDefault news={item} index={index} />
        </motion.div>
      ))}
    </div>
  ) : (
    <div className="w-full h-full flex justify-center items-center">
      <MyEmpty 
        link={'/blog'} 
        text={''} 
        description={t('nav.Noblog')} 
        bottomText={t('nav.Return_to_blog')}  
        className="py-20 max-md:py-4"
      >
        <i className="ri-draft-line text-4xl  !text-ddd"></i>
      </MyEmpty>
    </div>
  );
};



export default BlogList;
